//copied from tooshle and changed (todo: add to UI, why importing from ui doesn't work?)
import { FocusEventHandler, useCallback, useState } from "react";
import { digitsToHindi, digitsToLatin } from "@bale/utils";
import { ContainsEnglish, NotAllDigitsEnglish, NumberOnly } from "utils/regex";

export enum GeneralErrors {
  MinimumLength = "MinimumLength",
  MaximumLength = "MaximumLength",
  FixedLength = "FixedLength",
  MinimumAmount = "MinimumAmount",
  MaximumAmount = "MaximumAmount",
  Required = "Required",
  RegexMatch = "RegexMatch",
  ExcludeWithRegex = "ExcludeRegex",
  NoAnyEnglish = "NoANyEnglish",
  Phone = "Phone",
  NationalCode = "NationalCode",
  MediaType = "MediaType",
  NotAllEnglish = "NotAllEnglish",
  divisibleBy = "DivisibleBy",
}

export const GeneralValidators = {
  divisibleBy: (n: number) => (value: string) =>
    parseInt(value) % n !== 0 ? GeneralErrors.divisibleBy : undefined,
  minAmount: (minimumAmount: number) => (value: string) =>
    parseInt(value) < minimumAmount ? GeneralErrors.MinimumAmount : undefined,
  maxAmount: (maximumAmount: number) => {
    return (value: string) =>
      parseInt(value) > maximumAmount ? GeneralErrors.MaximumAmount : undefined;
  },
  minLength: (minimumLength: number) => {
    return (value: string) =>
      value.length < minimumLength ? GeneralErrors.MinimumLength : undefined;
  },
  maxLength: (maximumLength: number) => {
    return (value: string) =>
      value.length > maximumLength ? GeneralErrors.MaximumLength : undefined;
  },
  fixedLength: (fixedLength: number) => {
    return (value: string) =>
      value.length !== fixedLength ? GeneralErrors.FixedLength : undefined;
  },
  required: (value: string) => {
    return !value.trim() ? GeneralErrors.Required : undefined;
  },
  regexMatch: (regex: RegExp) => {
    return (value: string) =>
      !regex.test(value) ? GeneralErrors.RegexMatch : undefined;
  },

  NotAnyEnglish: (value: string) => {
    return ContainsEnglish.test(value) ? GeneralErrors.NoAnyEnglish : undefined;
  },

  excludeWithRegex: (regex: RegExp) => (value: string) =>
    regex.test(value) ? GeneralErrors.ExcludeWithRegex : undefined,

  NotAllEnglish: () => {
    return (value: string) => {
      if (NotAllDigitsEnglish.test(value)) {
        return GeneralErrors.NotAllEnglish;
      }
    };
  },
  phone: (value: string) =>
    (value.startsWith("9") && value.length === 10) ||
    (value.startsWith("09") && value.length === 11)
      ? undefined
      : GeneralErrors.Phone,
  nationalCode: (value: string) => {
    if (value.length !== 10) return GeneralErrors.NationalCode;
    const controlDigit = parseInt(value[0]);
    let sum = 0;
    for (let i = 1; i < value.length; i++) {
      sum = parseInt(value[i]) * (i + 1);
    }
    const reminder = sum % 11;
    if (reminder < 2)
      return reminder === controlDigit ? undefined : GeneralErrors.NationalCode;
    else
      return 11 - reminder === controlDigit
        ? undefined
        : GeneralErrors.NationalCode;
  },
};

type ValidationRule<T = GeneralErrors> = (value: string) => T | undefined;

type Inputs<T> = {
  initialValue?: string;
  beforeChange?: (value: string) => string;
  beforeBlur?: (value: string) => boolean | void;
  beforeFocus?: (value: string) => boolean | void;
  errorMessages?: {
    [key in string]: string;
  };
  rules?: ValidationRule<T | GeneralErrors>[];
  formatter?: (value: string) => string;
  //defaut behavior:
  //show digits in hindi | get digits in latin (by using rawValue)
  //can be changed if below inputs are included:
  getDigitsHindi?: boolean;
  showDigitsLatin?: boolean; //should be used for links e.g
  numberOnly?: boolean;
};

export type Outputs = {
  onChange: (value: string) => void;
  onFocus?: React.FocusEventHandler;
  onBlur?: React.FocusEventHandler;
  error: string | undefined;
  errorText: string | undefined; // for NewTextFiled
  value: string;
  rawValue: string;
  setError: (error?: string) => void;
  setValue: (value: string) => void;
  validate: () => boolean;
  required?: boolean; //used for disabling submit button
};
export const useInputHandler = <T>(input?: Inputs<T>): Outputs => {
  const [value, setValue] = useState(input?.initialValue ?? "");
  const [error, setError] = useState<string | undefined>(undefined);

  const validate = useCallback(() => {
    let err;
    if (!input?.rules) {
      return true;
    }
    for (const rule of input.rules) {
      err = rule(value);
      if (err) {
        setError(input?.errorMessages?.[err as string]); //TODO
        return false;
      }
    }
    return true;
  }, [value]);

  const format = useCallback((text: string) => {
    text = input?.formatter?.(text) ?? text;
    if (!input?.showDigitsLatin) {
      text = digitsToHindi(text);
    }
    return text;
  }, []);

  const unformat = useCallback(
    (text: string) => {
      if (!input?.getDigitsHindi) {
        text = digitsToLatin(text);
      }
      return input?.beforeChange?.(text) ?? text;
    },
    [input?.beforeChange]
  );

  const onChange = useCallback(
    (text: string) => {
      const unformattedText = unformat(text);
      if (input?.numberOnly && !NumberOnly.test(unformattedText)) return;
      setError(undefined);
      setValue(unformattedText);
    },
    [input?.beforeChange]
  );

  const onBlur: FocusEventHandler = useCallback(
    (event) => {
      if (input?.beforeBlur?.(value)) return;
      validate();
    },
    [value]
  );

  const onFocus: FocusEventHandler = useCallback(
    (event) => {
      if (input?.beforeFocus?.(value)) return;
      //
    },
    [value]
  );

  return {
    onChange,
    onFocus,
    onBlur,
    error,
    errorText: error,
    rawValue: value,
    value: format(value),
    setError,
    setValue,
    validate,
    required: input?.rules?.includes(GeneralValidators.required),
  };
};
